import { Component, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * La rete sotto una schermata che cade.
 *
 * Senza, un'eccezione in un solo componente svuota **tutto**: React smonta
 * l'albero e resta una pagina bianca, senza barra, senza navigazione e senza
 * un modo di andare altrove che ricaricare. Su questa console la pagina bianca
 * è particolarmente cattiva, perché arriva a qualcuno che potrebbe avere una
 * persona in linea: quello che deve restare vero, e che il testo dice, è che
 * la conversazione non si è interrotta — il media vive nella scheda, non in
 * questo albero di componenti.
 *
 * Sta **dentro** la barra e non intorno: se cade una sezione, le altre restano
 * raggiungibili. È l'unica ragione per cui vale la pena averla.
 *
 * È una classe perché in React non esiste altro modo: `componentDidCatch` non
 * ha un equivalente fra gli hook, e non c'è nessuna dipendenza da aggiungere
 * per una cosa che il framework fa già.
 */
type Props = {
  /**
   * Cambia quando si cambia sezione.
   *
   * Senza, una sezione caduta resterebbe caduta anche dopo essere andati
   * altrove e tornati: lo stato d'errore è del confine, non della schermata, e
   * nessuno lo azzererebbe mai. Con la chiave, cambiare sezione ricostruisce
   * il confine da capo — che è esattamente ciò che una persona si aspetta
   * quando riprova.
   */
  resetKey: string;
  children: ReactNode;
};

type State = { failed: boolean };

/**
 * Il messaggio, in un componente suo.
 *
 * `useTranslation` è un hook e una classe non può chiamarlo: la traduzione sta
 * qui, e il confine si limita a decidere quando disegnarlo.
 */
function ScreenFailure({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <main className="page" id="main">
      <div className="card">
        {/*
          Il fuoco arriva qui: la schermata è cambiata sotto le mani di chi
          guardava, e chi non guarda deve sapere che cos'è comparso al posto di
          quello che stava usando.
        */}
        {/* biome-ignore lint/a11y/noAutofocus: non è un modulo che ruba il fuoco
            all'apertura di una pagina — è un errore che sostituisce ciò che una
            persona stava usando, e il fuoco deve seguirlo (WCAG 3.2.2 riguarda
            i cambi di contesto non richiesti, non l'annuncio di un guasto). */}
        <h2 tabIndex={-1} autoFocus>
          {t('crash.title')}
        </h2>
        <p className="card__sub">{t('crash.body')}</p>
        <button type="button" className="button button--primary" onClick={onRetry}>
          {t('crash.retry')}
        </button>
      </div>
    </main>
  );
}

export class ScreenBoundary extends Component<Props, State> {
  override state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  override componentDidUpdate(previous: Props): void {
    // Sezione cambiata: si riparte puliti.
    if (previous.resetKey !== this.props.resetKey && this.state.failed) {
      this.setState({ failed: false });
    }
  }

  /*
    Niente `componentDidCatch` che registra da qualche parte.

    Un guasto qui dentro può portarsi dietro il nome di chi risponde, il testo
    di un messaggio o l'identificativo di una sessione: mandarlo a un servizio
    di monitoraggio sarebbe scrivere un dato personale in un log, che è
    l'ultima riga di "cosa non fare mai". L'eccezione resta nella console del
    browser, dove la vede solo chi ha quel dispositivo in mano.
  */

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return <ScreenFailure onRetry={() => this.setState({ failed: false })} />;
  }
}
